import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Site, SiteInsert, SiteUpdate } from '@/types/site';
import { toast } from 'sonner';

export interface OrgUser {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: 'admin' | 'contributor' | 'viewer';
}

interface SiteState {
  sites: Site[];
  loading: boolean;
  orgUsers: OrgUser[];

  loadSites: () => Promise<void>;
  loadOrgUsers: () => Promise<OrgUser[]>;
  addSite: (site: SiteInsert) => Promise<Site | null>;
  updateSite: (id: string, updates: SiteUpdate) => Promise<void>;
  deleteSite: (id: string) => Promise<void>;
  getSiteById: (id: string) => Site | undefined;
  calculateSiteVulnerability: (siteId: string) => Promise<void>;
}

export const useSiteStore = create<SiteState>()((set, get) => ({
  sites: [],
  loading: false,
  orgUsers: [],

  loadSites: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .order('name');

      if (error) throw error;

      // Fetch owner details for each site
      const sitesWithProfiles = await Promise.all(
        (data || []).map(async (site: any) => {
          const siteWithProfile = { ...site };

          // Get owner profile if owner_id exists
          if (site.owner_id) {
            const { data: ownerProfile } = await supabase
              .from('profiles')
              .select('user_id, name, email')
              .eq('user_id', site.owner_id)
              .single();

            if (ownerProfile) {
              const { data: roleData } = await supabase
                .from('user_roles')
                .select('role')
                .eq('user_id', ownerProfile.user_id)
                .single();

              siteWithProfile.owner_profile = {
                id: ownerProfile.user_id,
                name: ownerProfile.name || ownerProfile.email,
                email: ownerProfile.email,
                role: roleData?.role || 'viewer',
              };
            }
          }

          // Get creator profile if created_by exists
          if (site.created_by) {
            const { data: creatorProfile } = await supabase
              .from('profiles')
              .select('user_id, name')
              .eq('user_id', site.created_by)
              .single();

            if (creatorProfile) {
              siteWithProfile.creator = {
                id: creatorProfile.user_id,
                name: creatorProfile.name,
              };
            }
          }

          // Get updater profile if updated_by exists
          if (site.updated_by) {
            const { data: updaterProfile } = await supabase
              .from('profiles')
              .select('user_id, name')
              .eq('user_id', site.updated_by)
              .single();

            if (updaterProfile) {
              siteWithProfile.updater = {
                id: updaterProfile.user_id,
                name: updaterProfile.name,
              };
            }
          }

          return siteWithProfile;
        })
      );

      set({ sites: sitesWithProfiles as unknown as Site[] });
    } catch (error) {
      console.error('Error loading sites:', error);
      toast.error('Failed to load sites');
    } finally {
      set({ loading: false });
    }
  },

  loadOrgUsers: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, name, email')
        .eq('org_id', profile.org_id);

      if (!profiles) return [];

      const usersWithRoles = await Promise.all(
        profiles.map(async (p: any) => {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', p.user_id)
            .single();

          return {
            id: p.user_id,
            user_id: p.user_id,
            name: p.name || p.email,
            email: p.email,
            role: roleData?.role || 'viewer',
          } as OrgUser;
        })
      );

      set({ orgUsers: usersWithRoles });
      return usersWithRoles;
    } catch (error) {
      console.error('Error loading org users:', error);
      return [];
    }
  },

  addSite: async (site) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const insertData: any = {
        name: site.name,
        address: site.address,
        site_code: site.site_code,
        timezone: site.timezone,
        criticality: site.criticality,
        country: site.country,
        owner_id: site.owner_id || null,
        notes: site.notes,
        metadata: site.metadata,
        org_id: profile.org_id,
        created_by: userData.user.id,
        updated_by: userData.user.id,
      };

      const { data, error } = await supabase
        .from('sites')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      await get().loadSites();
      toast.success('Site created successfully');
      return data as Site;
    } catch (error: any) {
      console.error('Error creating site:', error);
      toast.error(error.message || 'Failed to create site');
      return null;
    }
  },

  updateSite: async (id, updates) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return;
      }

      const updateData: any = {
        name: updates.name,
        address: updates.address,
        site_code: updates.site_code,
        timezone: updates.timezone,
        criticality: updates.criticality,
        country: updates.country,
        owner_id: updates.owner_id ?? null,
        notes: updates.notes,
        metadata: updates.metadata,
        updated_by: userData.user.id,
      };

      const { error } = await supabase
        .from('sites')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      await get().loadSites();
      toast.success('Site updated successfully');
    } catch (error: any) {
      console.error('Error updating site:', error);
      toast.error(error.message || 'Failed to update site');
    }
  },

  deleteSite: async (id) => {
    try {
      const { error } = await supabase
        .from('sites')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        sites: state.sites.filter(s => s.id !== id)
      }));
      toast.success('Site deleted successfully');
    } catch (error: any) {
      console.error('Error deleting site:', error);
      toast.error(error.message || 'Failed to delete site');
    }
  },

  getSiteById: (id) => {
    return get().sites.find(s => s.id === id);
  },

  calculateSiteVulnerability: async (siteId) => {
    try {
      const { data: risks } = await supabase
        .from('risks')
        .select('risk_score')
        .eq('related_to_id', siteId)
        .eq('related_to_type', 'site');

      if (!risks || risks.length === 0) {
        await supabase
          .from('sites')
          .update({
            vulnerability_score: 0,
            vulnerability_level: 'Low'
          })
          .eq('id', siteId);
        await get().loadSites();
        return;
      }

      const totalScore = risks.reduce((sum, risk) => sum + (risk.risk_score || 0), 0);
      const avgScore = totalScore / risks.length;

      let level: 'Low' | 'Medium' | 'High' | 'Critical';
      if (avgScore >= 15) level = 'Critical';
      else if (avgScore >= 10) level = 'High';
      else if (avgScore >= 5) level = 'Medium';
      else level = 'Low';

      await supabase
        .from('sites')
        .update({
          vulnerability_score: Math.round(avgScore),
          vulnerability_level: level
        })
        .eq('id', siteId);

      await get().loadSites();
    } catch (error) {
      console.error('Error calculating site vulnerability:', error);
    }
  },
}));
